import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ArrowLeft, Plus, X, Save, Loader2, Activity, Calendar,
  Layers, ToggleLeft, ToggleRight, AlertCircle,
} from 'lucide-react';
import { API } from '../config/api';

/**
 * AddAthleticTests — manual entry of athletic test results.
 *
 * Simple mode  : pick one test, enter one value, see live grade.
 * Advanced mode: pick a grouped bundle (e.g. CMJ full) and enter ALL related
 *                metrics at once. The backend grades each row independently.
 */
export default function AddAthleticTests() {
  const navigate = useNavigate();
  const { childId } = useParams();

  const [child, setChild] = useState(null);
  const [catalog, setCatalog] = useState({ tests: [], groups: {} });
  const [advancedMode, setAdvancedMode] = useState(false);
  const [testDate, setTestDate] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([{ test_name: '', result_value: '', notes: '', grade: null, grade_meta: null }]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState({}); // rowIndex → bool

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const [childRes, catalogRes] = await Promise.all([
        axios.get(`${API}/children/${childId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/children/catalog/athletic-tests`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setChild(childRes.data);
      setCatalog(catalogRes.data);
    } catch (err) {
      toast.error('Failed to load athlete or test catalog');
      console.error(err);
    }
  }, [childId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // === Row helpers ===
  const updateRow = (idx, patch) => {
    setRows(prev => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const addRow = () => setRows(prev => [...prev, { test_name: '', result_value: '', notes: '', grade: null, grade_meta: null }]);
  const removeRow = (idx) => setRows(prev => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx));

  // === Live grade preview ===
  const fetchGradePreview = async (idx) => {
    const row = rows[idx];
    if (!row.test_name || !row.result_value) {
      updateRow(idx, { grade: null, grade_meta: null });
      return;
    }
    setLoadingPreview(prev => ({ ...prev, [idx]: true }));
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/children/${childId}/athletic-tests/preview-grade`, {
        params: { test_name: row.test_name, result_value: parseFloat(row.result_value), test_date: new Date(testDate).toISOString() },
        headers: { Authorization: `Bearer ${token}` },
      });
      updateRow(idx, { grade: res.data.grade, grade_meta: res.data });
    } catch (err) {
      updateRow(idx, { grade: null, grade_meta: null });
    } finally {
      setLoadingPreview(prev => ({ ...prev, [idx]: false }));
    }
  };

  // === Switching to a grouped bundle (advanced mode) ===
  const applyGroup = (groupName) => {
    setSelectedGroup(groupName);
    if (!groupName) {
      setRows([{ test_name: '', result_value: '', notes: '', grade: null, grade_meta: null }]);
      return;
    }
    const testsInGroup = catalog.groups[groupName] || [];
    setRows(testsInGroup.map(tn => ({ test_name: tn, result_value: '', notes: '', grade: null, grade_meta: null })));
  };

  // === Submit ===
  const submit = async () => {
    const validRows = rows.filter(r => r.test_name && r.result_value !== '' && !isNaN(parseFloat(r.result_value)));
    if (validRows.length === 0) {
      toast.error('Add at least one test with a numeric value');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/children/${childId}/athletic-tests`,
        {
          test_date: new Date(testDate).toISOString(),
          tests: validRows.map(r => ({
            test_name: r.test_name,
            result_value: parseFloat(r.result_value),
            notes: r.notes || null,
          })),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { inserted_count, skipped_count, skipped } = res.data;
      if (inserted_count > 0) {
        toast.success(`Saved ${inserted_count} test${inserted_count === 1 ? '' : 's'}${skipped_count ? ` · ${skipped_count} skipped` : ''}`);
        if (skipped_count > 0) {
          console.warn('Skipped tests:', skipped);
        }
        navigate(`/child/${childId}`);
      } else {
        toast.error('No tests were saved. Check field values and try again.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save tests');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  // === Render ===
  const gradeColor = (g) => {
    if (!g) return 'bg-zinc-800 text-zinc-500';
    return {
      5: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
      4: 'bg-lime-500/20 text-lime-300 border-lime-500/40',
      3: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
      2: 'bg-orange-500/20 text-orange-300 border-orange-500/40',
      1: 'bg-red-500/20 text-red-300 border-red-500/40',
    }[g] || 'bg-zinc-800 text-zinc-400';
  };

  const childName = child ? `${child.first_name} ${child.last_name}` : 'Athlete';

  return (
    <div className="min-h-screen bg-zinc-950 text-white pb-20">
      {/* Sticky header */}
      <div className="sticky top-0 z-30 bg-zinc-950/95 backdrop-blur-md border-b border-zinc-800">
        <div className="max-w-4xl mx-auto p-3 sm:p-4 flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate(`/child/${childId}`)}
            className="text-zinc-400 hover:text-white h-10 px-2 sm:px-3"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5 sm:mr-2" />
            <span className="hidden sm:inline">Back</span>
          </Button>
          <div className="text-center flex-1 min-w-0">
            <h1 className="text-base sm:text-xl font-bold truncate" data-testid="page-title">Add Athletic Tests</h1>
            <p className="text-xs text-zinc-500 truncate">{childName}{child?.sport ? ` · ${child.sport}` : ''}</p>
          </div>
          <Button
            onClick={submit}
            disabled={submitting}
            className="h-10 px-3 sm:px-5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold uppercase text-xs"
            data-testid="submit-tests-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin sm:mr-2" /> : <Save className="h-4 w-4 sm:mr-2" />}
            <span className="hidden sm:inline">Save Tests</span>
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-3 sm:p-6 space-y-4">
        {/* Date + mode toggle */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-400 mb-1.5 block">
              <Calendar className="inline h-3 w-3 mr-1" />Test session date
            </Label>
            <Input
              type="date"
              value={testDate}
              onChange={e => setTestDate(e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
              className="h-12 bg-zinc-900 border-zinc-800 text-white"
              data-testid="test-date-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-400 mb-1.5 block">Entry mode</Label>
            <button
              type="button"
              onClick={() => { setAdvancedMode(prev => !prev); setSelectedGroup(''); setRows([{ test_name: '', result_value: '', notes: '', grade: null, grade_meta: null }]); }}
              className="w-full h-12 px-3 bg-zinc-900 border border-zinc-800 rounded-md text-left flex items-center justify-between hover:bg-zinc-800/50"
              data-testid="mode-toggle-btn"
            >
              <span className="text-sm">{advancedMode ? 'Advanced (grouped bundle)' : 'Simple (single test rows)'}</span>
              {advancedMode ? <ToggleRight className="h-6 w-6 text-emerald-400" /> : <ToggleLeft className="h-6 w-6 text-zinc-600" />}
            </button>
          </div>
        </div>

        {/* Advanced-mode bundle picker */}
        {advancedMode && (
          <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
            <Label className="text-xs uppercase tracking-wider text-blue-300 mb-2 flex items-center gap-1">
              <Layers className="h-3 w-3" /> Select a grouped test bundle
            </Label>
            <select
              value={selectedGroup}
              onChange={e => applyGroup(e.target.value)}
              className="w-full h-12 bg-zinc-900 border border-zinc-800 text-white rounded-md px-3 text-sm"
              data-testid="group-select"
            >
              <option value="">-- Choose a bundle --</option>
              {Object.keys(catalog.groups).map(g => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
            {selectedGroup && (
              <p className="mt-2 text-xs text-blue-300/70">
                {catalog.groups[selectedGroup]?.length} test rows pre-filled below. Enter values for each.
              </p>
            )}
          </div>
        )}

        {/* Test rows */}
        <div className="space-y-2">
          {rows.map((row, idx) => {
            const meta = catalog.tests.find(t => t.name === row.test_name);
            return (
              <div
                key={idx}
                className="p-3 sm:p-4 bg-zinc-900/60 border border-zinc-800 rounded-lg"
                data-testid={`test-row-${idx}`}
              >
                <div className="grid grid-cols-12 gap-2 sm:gap-3 items-start">
                  {/* Test selector */}
                  <div className="col-span-12 sm:col-span-6">
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Test</Label>
                    <select
                      value={row.test_name}
                      onChange={e => updateRow(idx, { test_name: e.target.value, grade: null, grade_meta: null })}
                      onBlur={() => fetchGradePreview(idx)}
                      disabled={advancedMode && selectedGroup}
                      className="w-full h-11 bg-zinc-900 border border-zinc-800 text-white rounded-md px-2 text-sm"
                      data-testid={`test-select-${idx}`}
                    >
                      <option value="">-- Select test --</option>
                      {catalog.tests.map(t => (
                        <option key={t.name} value={t.name}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                  {/* Value */}
                  <div className="col-span-7 sm:col-span-3">
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">
                      Value {meta?.unit ? <span className="text-zinc-600">({meta.unit})</span> : null}
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      placeholder={meta?.lower_is_better ? 'e.g. 1.85' : 'e.g. 45.5'}
                      value={row.result_value}
                      onChange={e => updateRow(idx, { result_value: e.target.value, grade: null, grade_meta: null })}
                      onBlur={() => fetchGradePreview(idx)}
                      className="h-11 bg-zinc-900 border-zinc-800 text-white font-mono"
                      data-testid={`test-value-${idx}`}
                    />
                  </div>
                  {/* Grade preview */}
                  <div className="col-span-5 sm:col-span-2">
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Grade</Label>
                    <div
                      className={`h-11 rounded-md border flex items-center justify-center font-bold text-lg ${gradeColor(row.grade)}`}
                      data-testid={`grade-preview-${idx}`}
                    >
                      {loadingPreview[idx] ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : row.grade ? (
                        `${row.grade}/5`
                      ) : (
                        <span className="text-xs">—</span>
                      )}
                    </div>
                  </div>
                  {/* Remove */}
                  <div className="col-span-12 sm:col-span-1 flex sm:justify-end">
                    {rows.length > 1 && !(advancedMode && selectedGroup) && (
                      <Button
                        variant="ghost"
                        onClick={() => removeRow(idx)}
                        className="h-11 w-11 p-0 text-red-400 hover:bg-red-500/10 sm:mt-5"
                        data-testid={`remove-row-${idx}`}
                      >
                        <X className="h-5 w-5" />
                      </Button>
                    )}
                  </div>
                </div>
                {/* Grade meta line */}
                {row.grade_meta && (
                  <p className="mt-2 text-[11px] text-zinc-500">
                    Age group: <span className="text-zinc-300">{row.grade_meta.age_group}</span>
                    {row.grade_meta.benchmark_range && (
                      <> · Benchmark: <span className="text-zinc-300">{row.grade_meta.benchmark_range}</span></>
                    )}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Add row button (only in simple mode or advanced w/o group) */}
        {(!advancedMode || !selectedGroup) && (
          <Button
            variant="outline"
            onClick={addRow}
            className="w-full h-12 border-dashed border-zinc-700 text-zinc-400 hover:bg-zinc-900"
            data-testid="add-row-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add another test
          </Button>
        )}

        {/* Helper banner */}
        <div className="flex items-start gap-2 p-3 rounded bg-zinc-900/40 border border-zinc-800">
          <AlertCircle className="h-4 w-4 text-zinc-500 mt-0.5 shrink-0" />
          <p className="text-xs text-zinc-500 leading-relaxed">
            Grades are calculated live against {child?.gender || 'sex'}- and age-specific benchmarks.
            Save when ready — values feed into the next Athlete Dossier automatically.
          </p>
        </div>
      </div>
    </div>
  );
}
